import React, { useCallback, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';

import { FlowCta } from 'src/components/jump-page/flow-cta';
import {
  DEFAULT_LABEL,
  GoalOption,
  options,
  optionsWithDefault,
} from 'src/components/jump-page/hero-section/inner-cta-section/cta-component';

import {
  useCallbackEvent,
  useSegmentPageAction,
} from 'src/utils/analytics/hooks';
import { useBrandedLPAnalytics } from '../../context';
import { getInputs } from 'src/redux/selectors';

import { Input } from 'src/lib/constants';
import {
  EventType,
  FormName,
  PageActionName,
  PageActionType,
  TrkLocation,
} from 'src/utils/analytics/constants';
import { PrimaryGoal } from 'src/types/inputs';
import { useAppSelector } from 'src/redux/hooks';

const texts = Object.freeze({
  ctaLabel: 'Get started',
});

const PrimaryGoalCTA = () => {
  const inputs = useAppSelector(getInputs);
  const [selectedOption, setSelectedOption] = useState<GoalOption>(
    (inputs?.[Input.PrimaryGoal] ?? '') as GoalOption
  );
  const trackPageAction = useSegmentPageAction();
  const theme = useTheme();
  const { navigationId } = useBrandedLPAnalytics();

  const trackEvent = useCallbackEvent({
    navigationId,
  });

  const handleChange = useCallback(
    (e: SelectChangeEvent<PrimaryGoal>) => {
      const value = e?.target?.value;
      trackPageAction({
        actionName: PageActionName.PrimaryGoal,
        actionType: PageActionType.Select,
        actionInput: value,
      });
      trackEvent(EventType.ShoppingGenericUpdated, {
        form_name: FormName.Flow,
        page_number: 1,
        total_pages: undefined,
        is_valid: true,
        validation_failure_message: '',
        was_prefilled: false,
        additional_attributes: {
          answer_value: value,
          question_name: Input.PrimaryGoal,
        },
      });
      setSelectedOption(value as GoalOption);
    },
    [trackEvent, trackPageAction]
  );

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      bgcolor="green"
      width="100%"
      paddingX={{ xs: 2, md: 0 }}
      justifyContent={{ xs: 'center', xl: 'flex-start' }}
    >
      <FormControl
        sx={{
          [theme.breakpoints.up('md')]: {
            m: 1,
            width: 450,
            minWidth: 300,
          },
          zIndex: 0,
        }}
        size="small"
      >
        <InputLabel
          htmlFor="primary-goal-select"
          sx={{
            color: !selectedOption ? 'neutral.light' : 'text.primary',
            borderColor: !selectedOption ? 'neutral.light' : 'text.primary',
            lineHeight: '1.7rem',
          }}
        >
          {DEFAULT_LABEL}
        </InputLabel>
        <Select
          id="primary-goal-select"
          label={DEFAULT_LABEL}
          value={selectedOption}
          onChange={handleChange}
          sx={{
            lineHeight: '1.7rem',
            backgroundColor: 'white',
            marginRight: 1,
            borderRadius: 1,
            color: !selectedOption ? 'neutral.light' : 'text.primary',
            borderColor: !selectedOption ? 'neutral.light' : 'text.primary',
            [theme.breakpoints.down('md')]: {
              marginBottom: 1,
              marginRight: 0,
            },
          }}
        >
          {(selectedOption ? options : optionsWithDefault).map((opt) => (
            <MenuItem key={opt.label} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FlowCta
        primaryGoal={selectedOption}
        trkLocation={TrkLocation.JumpPageHero}
        sx={{
          borderRadius: 1,
          height: '2.75rem',
          marginTop: 1,
          paddingRight: 3,
          [theme.breakpoints.up('md')]: {
            maxWidth: '8rem',
            marginX: 0,
            padding: 0,
          },
        }}
      >
        <Typography fontSize={14} variant="accentSmall">
          {texts.ctaLabel}
        </Typography>
      </FlowCta>
    </Box>
  );
};

export default PrimaryGoalCTA;
